import React, { useState, useEffect, useCallback } from 'react';
import { Card, LoadingSkeleton } from '../components/ui';
import { BuildingOffice2Icon, CheckCircleIcon, NoSymbolIcon, UsersIcon, UserGroupIcon, AcademicCapIcon } from '@heroicons/react/24/outline';

const statConfig = [
  { key: 'total_schools', label: 'Total Schools', icon: BuildingOffice2Icon, color: '#4C1D95' },
  { key: 'active_schools', label: 'Active Schools', icon: CheckCircleIcon, color: '#10B981' },
  { key: 'suspended_schools', label: 'Suspended Schools', icon: NoSymbolIcon, color: '#DC2626' },
  { key: 'total_users', label: 'Total Users', icon: UsersIcon, color: '#3B82F6' },
  { key: 'total_students', label: 'Total Students', icon: UserGroupIcon, color: '#1E40AF' },
  { key: 'total_teachers', label: 'Total Teachers', icon: AcademicCapIcon, color: '#D97706' },
];

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
        borderRadius: 'var(--radius-lg)',
        padding: '1.5rem',
        color: 'white',
        boxShadow: `0 4px 12px ${color}44`,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: '0.8rem', opacity: 0.85, marginBottom: '0.5rem' }}>{label}</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>{(value ?? 0).toLocaleString()}</div>
        </div>
        <Icon style={{ width: 32, height: 32, opacity: 0.3 }} />
      </div>
    </div>
  );
}

function SystemOverview({ token }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/superadmin/metrics/overview', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setMetrics(await response.json());
      } else {
        setError('Failed to load system metrics');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>System Overview</h2>
        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
          Platform-wide metrics across every school.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <LoadingSkeleton type="stat" rows={6} />
      ) : metrics ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          {statConfig.map(({ key, ...stat }) => (
            <StatCard key={key} {...stat} value={metrics[key]} />
          ))}
        </div>
      ) : (
        <Card>No metrics available.</Card>
      )}
    </div>
  );
}

export default SystemOverview;
